import { useEffect, useState } from 'react'

import CardSurface from '../../components/card-surface/CardSurface'
import {
  getCommunityPostReplies,
  removeCommunityPost,
  replyToCommunityPost,
  reportCommunityPost,
  setCommunityPostResolution,
  voteFeedPost,
} from '../../_data/resident'
import {
  RenterPost,
  RenterReplyView,
  ResidentPostReportReason,
} from '../../_types/resident'
import { REPORT_REASONS, formatPostWhen } from './helpers'
import {
  PostTitleRow,
  PostTitle,
  ChipRow,
  CategoryChip,
  ResolvedBadge,
  AdminBadge,
  PostMeta,
  PostBody,
  ActionRow,
  ActionButton,
  Divider,
  ReplyList,
  ReplyItem,
  ReplyHandleRow,
  ReplyHandle,
  ReplyBody,
  ReplyForm,
  ReportPanel,
  InlineNote,
  QuietText,
  TextArea,
  SelectInput,
  FieldLabel,
  PrimaryButton,
  GhostButton,
  ComposerActions,
  ErrorBanner,
  VoteControl,
  VoteButton,
  VoteScore,
  PostImageGrid,
  PostImageButton,
  PostImage,
  LightboxBackdrop,
  LightboxImage,
} from './styled'

interface PostCardProps {
  renterPost: RenterPost
  tenancyId: string
  onChanged: () => void
}

/**
 * A single community post card. Tapping the title expands it to the full body,
 * replies (the real-name contact channel), a reply box, a report control, and —
 * for the author — mark-resolved / remove. Manages its own expand + reply +
 * report state; calls onChanged after a mutation so the parent list refreshes.
 *
 * @param {PostCardProps} props - the post + tenancy + change callback
 * @returns {JSX.Element}
 */
const PostCard = ({
  renterPost,
  tenancyId,
  onChanged,
}: PostCardProps): JSX.Element => {
  const { post, isAuthor } = renterPost
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [replies, setReplies] = useState<RenterReplyView[]>([])
  const [isLoadingReplies, setIsLoadingReplies] = useState<boolean>(false)
  const [replyText, setReplyText] = useState<string>('')
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [reportReplyId, setReportReplyId] = useState<string | null>(null)
  const [isReportOpen, setIsReportOpen] = useState<boolean>(false)
  const [reportReason, setReportReason] =
    useState<ResidentPostReportReason>('inappropriate')
  const [reportDetail, setReportDetail] = useState<string>('')
  const [reportDone, setReportDone] = useState<boolean>(false)
  const [isVoting, setIsVoting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  useEffect(() => {
    if (lightboxUrl === null) {
      return undefined
    }

    const handleKeyDown = (keyboardEvent: KeyboardEvent): void => {
      if (keyboardEvent.key === 'Escape') {
        setLightboxUrl(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [lightboxUrl])

  const loadReplies = (): void => {
    setIsLoadingReplies(true)
    void getCommunityPostReplies(tenancyId, post.id).then(loaded => {
      setReplies(loaded)
      setIsLoadingReplies(false)
    })
  }

  const handleToggleExpand = (): void => {
    const next = isExpanded === false
    setIsExpanded(next)
    if (next === true) {
      loadReplies()
      return
    }
    // Collapsing resets the transient report state so a stale "report sent"
    // confirmation doesn't linger on the next expand.
    setReportDone(false)
    setIsReportOpen(false)
  }

  const handleSendReply = (): void => {
    if (replyText.trim() === '' || isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    void replyToCommunityPost(tenancyId, post.id, replyText.trim()).then(
      reply => {
        setIsBusy(false)
        if (reply === null) {
          setErrorMessage('We couldn’t post your reply — please try again.')
          return
        }
        setReplyText('')
        loadReplies()
        onChanged()
      },
    )
  }

  const openReport = (replyId: string | null): void => {
    setReportReplyId(replyId)
    setReportReason('inappropriate')
    setReportDetail('')
    setReportDone(false)
    setIsReportOpen(true)
  }

  const handleSubmitReport = (): void => {
    if (isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    void reportCommunityPost(
      tenancyId,
      post.id,
      reportReplyId,
      reportReason,
      reportDetail.trim(),
    ).then(wasReported => {
      setIsBusy(false)
      if (wasReported === false) {
        // Keep the panel open with the reason/detail intact so a retry is easy.
        setErrorMessage('We couldn’t send your report — please try again.')
        return
      }
      setIsReportOpen(false)
      setReportDone(true)
    })
  }

  const handleToggleResolution = (): void => {
    if (isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    const nextResolution = post.resolution === 'resolved' ? 'open' : 'resolved'
    void setCommunityPostResolution(tenancyId, post.id, nextResolution).then(
      updated => {
        setIsBusy(false)
        if (updated === null) {
          setErrorMessage('That didn’t work — please try again.')
          return
        }
        onChanged()
      },
    )
  }

  const handleRemove = (): void => {
    if (isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    void removeCommunityPost(tenancyId, post.id).then(wasRemoved => {
      setIsBusy(false)
      if (wasRemoved === false) {
        setErrorMessage('We couldn’t remove that — please try again.')
        return
      }
      onChanged()
    })
  }

  const handleVote = (value: 1 | -1): void => {
    if (isVoting === true) {
      return
    }
    setIsVoting(true)
    setErrorMessage('')
    void voteFeedPost(tenancyId, post.id, value).then(updated => {
      setIsVoting(false)
      if (updated === null) {
        setErrorMessage('We couldn’t record your vote — please try again.')
        return
      }
      // Refetch so the net score (and any community auto-hide) reflects the vote.
      onChanged()
    })
  }

  const replyCountLabel =
    post.replyCount === 1 ? '1 reply' : `${post.replyCount} replies`
  const netScore = post.upvoteCount - post.downvoteCount

  return (
    <>
      <CardSurface padding="standard">
        <PostTitleRow>
          <PostTitle type="button" onClick={handleToggleExpand}>
            {post.title}
          </PostTitle>
          {post.resolution === 'resolved' ? (
            <ResolvedBadge>Resolved</ResolvedBadge>
          ) : null}
        </PostTitleRow>

        <ChipRow>
          <CategoryChip>{post.category}</CategoryChip>
          {post.showAdminBadge ? <AdminBadge>Admin</AdminBadge> : null}
        </ChipRow>

        <PostMeta>
          {post.displayHandle} · {formatPostWhen(post.createdAt)} ·{' '}
          {replyCountLabel}
        </PostMeta>

        {post.imageUrls.length > 0 ? (
          <PostImageGrid>
            {post.imageUrls.map(url => (
              <PostImageButton
                key={url}
                type="button"
                onClick={() => setLightboxUrl(url)}
              >
                <PostImage src={url} alt={`Photo for ${post.title}`} />
              </PostImageButton>
            ))}
          </PostImageGrid>
        ) : null}

        {post.postType === 'feed' ? (
          <VoteControl>
            <VoteButton
              type="button"
              disabled={isVoting === true}
              onClick={() => handleVote(1)}
            >
              ▲
            </VoteButton>
            <VoteScore>{netScore}</VoteScore>
            <VoteButton
              type="button"
              disabled={isVoting === true}
              onClick={() => handleVote(-1)}
            >
              ▼
            </VoteButton>
          </VoteControl>
        ) : null}

        {isExpanded === true ? (
          <>
            <PostBody>{post.body}</PostBody>

            <ActionRow>
              {isAuthor === true ? (
                <>
                  <ActionButton
                    type="button"
                    disabled={isBusy === true}
                    onClick={handleToggleResolution}
                  >
                    {post.resolution === 'resolved'
                      ? 'Reopen'
                      : 'Mark resolved'}
                  </ActionButton>
                  <ActionButton
                    type="button"
                    tone="danger"
                    disabled={isBusy === true}
                    onClick={handleRemove}
                  >
                    Remove
                  </ActionButton>
                </>
              ) : (
                <ActionButton
                  type="button"
                  tone="danger"
                  onClick={() => openReport(null)}
                >
                  Report
                </ActionButton>
              )}
            </ActionRow>

            <Divider />

            {isLoadingReplies === true ? (
              <QuietText>Loading replies…</QuietText>
            ) : null}

            {isLoadingReplies === false && replies.length === 0 ? (
              <QuietText>No replies yet — be the first to reach out.</QuietText>
            ) : null}

            {replies.length > 0 ? (
              <ReplyList>
                {replies.map(reply => (
                  <ReplyItem key={reply.id}>
                    <ReplyHandleRow>
                      <ReplyHandle>{reply.displayHandle}</ReplyHandle>
                      {reply.showAdminBadge ? (
                        <AdminBadge>Admin</AdminBadge>
                      ) : null}
                      <GhostButton
                        type="button"
                        onClick={() => openReport(reply.id)}
                      >
                        Report
                      </GhostButton>
                    </ReplyHandleRow>
                    <ReplyBody>{reply.body}</ReplyBody>
                  </ReplyItem>
                ))}
              </ReplyList>
            ) : null}

            <ReplyForm>
              <TextArea
                placeholder="Write a reply…"
                value={replyText}
                onChange={event => setReplyText(event.target.value)}
              />
              <ComposerActions>
                <PrimaryButton
                  type="button"
                  disabled={isBusy === true || replyText.trim() === ''}
                  onClick={handleSendReply}
                >
                  Send reply
                </PrimaryButton>
              </ComposerActions>
            </ReplyForm>

            {isReportOpen === true ? (
              <ReportPanel>
                <FieldLabel>
                  {reportReplyId === null
                    ? 'Why are you reporting this post?'
                    : 'Why are you reporting this reply?'}
                </FieldLabel>
                <SelectInput
                  value={reportReason}
                  onChange={event =>
                    setReportReason(
                      event.target.value as ResidentPostReportReason,
                    )
                  }
                >
                  {REPORT_REASONS.map(reason => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </SelectInput>
                <TextArea
                  placeholder="Anything else the property manager should know? (optional)"
                  value={reportDetail}
                  onChange={event => setReportDetail(event.target.value)}
                />
                <ComposerActions>
                  <GhostButton
                    type="button"
                    onClick={() => setIsReportOpen(false)}
                  >
                    Cancel
                  </GhostButton>
                  <PrimaryButton
                    type="button"
                    disabled={isBusy === true}
                    onClick={handleSubmitReport}
                  >
                    Send report
                  </PrimaryButton>
                </ComposerActions>
              </ReportPanel>
            ) : null}

            {reportDone === true ? (
              <InlineNote>
                Thanks — your report was sent to the property manager.
              </InlineNote>
            ) : null}

            {errorMessage !== '' ? (
              <ErrorBanner>{errorMessage}</ErrorBanner>
            ) : null}
          </>
        ) : null}
      </CardSurface>

      {lightboxUrl !== null ? (
        <LightboxBackdrop
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxUrl(null)}
        >
          <LightboxImage src={lightboxUrl} alt="" />
        </LightboxBackdrop>
      ) : null}
    </>
  )
}

export default PostCard
